export const CONTENT_OPERATIONS_QUEUE_CATEGORIES =
  [
    "overdue_calendar_items",
    "due_this_week",
    "preparation_ready",
    "drafts_waiting_for_review",
    "approved_waiting_for_website",
    "website_waiting_for_buffer",
    "buffer_waiting_for_linkedin_confirmation",
  ] as const;

export type ContentOperationsQueueCategory =
  typeof CONTENT_OPERATIONS_QUEUE_CATEGORIES[number];

export interface ContentOperationsQueueItem {
  queueItemId:
    string;

  source:
    "calendar" |
    "article";

  calendarItemId:
    string |
    null;

  articleId:
    string |
    null;

  title:
    string;

  topic:
    string |
    null;

  priority:
    string |
    null;

  targetPublishAt:
    string |
    null;

  calendarStatus:
    string |
    null;

  articleStatus:
    string |
    null;

  articleUrl:
    string |
    null;

  reason:
    string;

  updatedAt:
    string;
}

export type ContentOperationsQueueCounts =
  Record<
    ContentOperationsQueueCategory,
    number
  >;

export type ContentOperationsQueues =
  Record<
    ContentOperationsQueueCategory,
    ContentOperationsQueueItem[]
  >;

export interface ContentOperationsQueueResponse {
  ok:
    true;

  mode:
    "content-operations-queue";

  referenceAt:
    string;

  dueWindowEndsAt:
    string;

  totalActionItems:
    number;

  counts:
    ContentOperationsQueueCounts;

  queues:
    ContentOperationsQueues;
}

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value ===
      "object" &&
    value !==
      null &&
    !Array.isArray(
      value,
    )
  );
}

function readString(
  value: unknown,
  fieldName: string,
): string {
  if (
    typeof value !==
      "string" ||
    !value.trim()
  ) {
    throw new Error(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value;
}

function readNullableString(
  value: unknown,
  fieldName: string,
): string | null {
  if (
    value ===
    null
  ) {
    return null;
  }

  return readString(
    value,
    fieldName,
  );
}

function parseQueueItem(
  value: unknown,
): ContentOperationsQueueItem {
  if (
    !isRecord(
      value,
    )
  ) {
    throw new Error(
      "A queue item must be an object.",
    );
  }

  const source =
    readString(
      value.source,
      "source",
    );

  if (
    source !==
      "calendar" &&
    source !==
      "article"
  ) {
    throw new Error(
      "source must be calendar or article.",
    );
  }

  return {
    queueItemId:
      readString(
        value.queueItemId,
        "queueItemId",
      ),

    source,

    calendarItemId:
      readNullableString(
        value.calendarItemId,
        "calendarItemId",
      ),

    articleId:
      readNullableString(
        value.articleId,
        "articleId",
      ),

    title:
      readString(
        value.title,
        "title",
      ),

    topic:
      readNullableString(
        value.topic,
        "topic",
      ),

    priority:
      readNullableString(
        value.priority,
        "priority",
      ),

    targetPublishAt:
      readNullableString(
        value.targetPublishAt,
        "targetPublishAt",
      ),

    calendarStatus:
      readNullableString(
        value.calendarStatus,
        "calendarStatus",
      ),

    articleStatus:
      readNullableString(
        value.articleStatus,
        "articleStatus",
      ),

    articleUrl:
      readNullableString(
        value.articleUrl,
        "articleUrl",
      ),

    reason:
      readString(
        value.reason,
        "reason",
      ),

    updatedAt:
      readString(
        value.updatedAt,
        "updatedAt",
      ),
  };
}

export function parseContentOperationsQueueResponse(
  value: unknown,
): ContentOperationsQueueResponse {
  if (
    !isRecord(
      value,
    )
  ) {
    throw new Error(
      "The queue response must be an object.",
    );
  }

  if (
    value.ok !==
      true ||
    value.mode !==
      "content-operations-queue"
  ) {
    throw new Error(
      "The queue response has an invalid mode.",
    );
  }

  if (
    !isRecord(
      value.counts,
    ) ||
    !isRecord(
      value.queues,
    )
  ) {
    throw new Error(
      "The queue response is missing counts or queues.",
    );
  }

  const counts =
    {} as ContentOperationsQueueCounts;

  const queues =
    {} as ContentOperationsQueues;

  let calculatedTotal =
    0;

  for (
    const category of
      CONTENT_OPERATIONS_QUEUE_CATEGORIES
  ) {
    const count =
      value.counts[category];

    const rawQueue =
      value.queues[category];

    if (
      typeof count !==
        "number" ||
      !Number.isInteger(
        count,
      ) ||
      count <
        0
    ) {
      throw new Error(
        `${category} must have a non-negative integer count.`,
      );
    }

    if (
      !Array.isArray(
        rawQueue,
      )
    ) {
      throw new Error(
        `${category} must be an array.`,
      );
    }

    const parsedQueue =
      rawQueue.map(
        parseQueueItem,
      );

    if (
      count !==
        parsedQueue.length
    ) {
      throw new Error(
        `${category} count does not match its queue length.`,
      );
    }

    counts[category] =
      count;

    queues[category] =
      parsedQueue;

    calculatedTotal +=
      count;
  }

  const totalActionItems =
    value.totalActionItems;

  if (
    typeof totalActionItems !==
      "number" ||
    !Number.isInteger(
      totalActionItems,
    ) ||
    totalActionItems <
      0 ||
    totalActionItems !==
      calculatedTotal
  ) {
    throw new Error(
      "totalActionItems does not match the queue counts.",
    );
  }

  return {
    ok:
      true,

    mode:
      "content-operations-queue",

    referenceAt:
      readString(
        value.referenceAt,
        "referenceAt",
      ),

    dueWindowEndsAt:
      readString(
        value.dueWindowEndsAt,
        "dueWindowEndsAt",
      ),

    totalActionItems,

    counts,

    queues,
  };
}

export async function fetchContentOperationsQueue(
  signal?: AbortSignal,
): Promise<ContentOperationsQueueResponse> {
  const response =
    await fetch(
      "/api/content-operations/queue",
      {
        method:
          "GET",

        headers: {
          Accept:
            "application/json",
        },

        cache:
          "no-store",

        signal,
      },
    );

  let body:
    unknown;

  try {
    body =
      await response.json();
  } catch {
    throw new Error(
      "The dashboard received an invalid API response.",
    );
  }

  if (
    !response.ok
  ) {
    const errorMessage =
      isRecord(
        body,
      ) &&
      typeof body.error ===
        "string"
        ? body.error
        : `Queue request failed with HTTP ${response.status}.`;

    throw new Error(
      errorMessage,
    );
  }

  return parseContentOperationsQueueResponse(
    body,
  );
}
